import React, { useState, useEffect, useMemo  } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../components/auth/auth';
import { Spinner, Alert, ButtonGroup, Button } from 'react-bootstrap';
import './StationDetails.css';

import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  CategoryScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line, Bar } from 'react-chartjs-2';
import DatePicker from "react-datepicker";
import { Dropdown, DropdownButton } from 'react-bootstrap';
import "react-datepicker/dist/react-datepicker.css";


ChartJS.register(
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const mergeModuleData = (modules) => {
    const merged = {};

    Object.entries(modules).forEach(([moduleName, readings]) => {
        readings.forEach(({ datetime, value_mean  }) => {
            if (!datetime) return;
            const iso = new Date(datetime).toISOString();  // use 'datetime' not 'timestamp'
            if (!merged[iso]) merged[iso] = { timestamp: iso };
            merged[iso][moduleName] = value_mean  ?? null;
        });
    });

    return Object.values(merged).sort((a, b) =>
        new Date(a.timestamp) - new Date(b.timestamp)
    );
};

function get_pointColor(value, type) {
    const ph_color_points = [
        { value: 0, hex: '#8D0A0A', stroke: 'rgba(141, 10, 10, 0.3)', fill: 'rgba(141, 10, 10, 0.5)' },
        { value: 1, hex: '#CF1212', stroke: 'rgba(207, 18, 18, 0.3)', fill: 'rgba(207, 18, 18, 0.5)' },
        { value: 2, hex: '#F51F1F', stroke: 'rgba(245, 31, 31, 0.3)', fill: 'rgba(245, 31, 31, 0.5)' },
        { value: 3, hex: '#F73E3E', stroke: 'rgba(247, 62, 62, 0.3)', fill: 'rgba(247, 62, 62, 0.5)' },
        { value: 4, hex: '#FF9F9F', stroke: 'rgba(255, 159, 159, 0.3)', fill: 'rgba(255, 159, 159, 0.5)' },
        { value: 5, hex: '#FFCC8A', stroke: 'rgba(255, 204, 138, 0.3)', fill: 'rgba(255, 204, 138, 0.5)' },
        { value: 6, hex: '#C4FED7', stroke: 'rgba(82, 206, 167, 0.3)', fill: 'rgba(82, 206, 167, 0.5)' },
        { value: 7, hex: '#8AFFB1', stroke: 'rgba(138, 255, 177, 0.3)', fill: 'rgba(138, 255, 177, 0.5)' },
        { value: 8, hex: '#C4FED7', stroke: 'rgba(196, 254, 215, 0.3)', fill: 'rgba(196, 254, 215, 0.5)' },
        { value: 9, hex: '#B6EDFC', stroke: 'rgba(182, 237, 252, 0.3)', fill: 'rgba(182, 237, 252, 0.5)' },
        { value: 10, hex: '#8AE6FF', stroke: 'rgba(138, 230, 255, 0.3)', fill: 'rgba(138, 230, 255, 0.5)' },
        { value: 11, hex: '#0EB0DC', stroke: 'rgba(14, 176, 220, 0.3)', fill: 'rgba(14, 176, 220, 0.5)' },
        { value: 12, hex: '#0E87A8', stroke: 'rgba(14, 135, 168, 0.3)', fill: 'rgba(14, 135, 168, 0.5)' },
        { value: 13, hex: '#097593', stroke: 'rgba(9, 117, 147, 0.3)', fill: 'rgba(9, 117, 147, 0.5)' },
        { value: 14, hex: '#085F77', stroke: 'rgba(8, 95, 119, 0.3)', fill: 'rgba(8, 95, 119, 0.5)' },
    ];

    return value >= 0 && value <= 14 ? ph_color_points[value][type] : 'grey';
}

const directionArrowPlugin = {
    id: 'directionArrows',
    afterDatasetsDraw(chart) {
        const { ctx, chartArea } = chart;
        const pluginOpts = chart.options.plugins.directionArrows;
        if (!pluginOpts?.enabled) return;

        chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (!chart.isDatasetVisible(datasetIndex)) return;

            meta.data.forEach((point, i) => {
                const angle = pluginOpts.angles[datasetIndex][i];
                console.log(angle);
                const value = dataset.data[i];

                if (value == null || point.skip) return;

                const rad = angle * (Math.PI / 180); // rotate to point upward
                ctx.save();
                ctx.translate(point.x, point.y);
                ctx.rotate(rad);
                ctx.beginPath();
                ctx.moveTo(0, -10);
                ctx.lineTo(6, 6);
                ctx.lineTo(0, 2);
                ctx.lineTo(-6, 6);
                ctx.closePath();
                ctx.fillStyle = dataset.borderColor ;
                ctx.fill();
                ctx.restore();
            });
        });
    },
};

const hslColors = [
  'hsl(354 100% 42%)',
  'hsl(223 100% 43%)',
  'hsl(133 100% 25%)',
  'hsl(33 30% 38%)',
  'hsl(161 56% 56%)',
  'hsl(270 66% 43%)',
  'hsl(35 100% 50%)',
  'hsl(160 95% 40%)',
  'hsl(199 90% 64%)',
  'hsl(349 100% 59%)'
];

const SensorGraph = ({ sensorType, modules, chartConfigs }) => {
    const [chartReady, setChartReady] = useState(false);
    const data = useMemo(() => mergeModuleData(modules), [modules]);
    const moduleNames = Object.keys(modules);
    const chartConfig = chartConfigs?.[sensorType] || {};
    const sensorName = chartConfig.name || sensorType || 'Sensor';
    const isPH = sensorName.trim().toLowerCase() === 'soil ph';
    const isWindDirection = sensorName.trim().toLowerCase() === 'wind direction';

    let chartData;
    let options;

    useEffect(() => {
        setChartReady(false);
        const timer = setTimeout(() => setChartReady(true), 100); // simulate render delay
        return () => clearTimeout(timer);
      }, [modules]);

    if (!chartReady) return <Spinner animation="border" className="d-block mb-3" />;

    if (isPH) {
        chartData = {
            labels: data.map(d => d.timestamp),
            datasets: moduleNames.map((mod, idx)=> ({
                label: mod,
                data: data.map(d => d[mod]),
                backgroundColor: data.map(d => get_pointColor(Math.round(d[mod]), 'fill')),
                borderColor: hslColors[idx % hslColors.length],
                borderWidth: 1.8,

            })),
        };

        options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: `${sensorName} History`,
                    font: { size: 18 },
                },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                },
                legend: { position: 'top' },
            },
            scales: {
                x: {
                    type: 'time',
                    stacked: true,
                    time: {
                        unit: 'hour',
                        tooltipFormat: 'PPpp',
                    },
                    ticks: {
                        autoSkip: false,
                        maxRotation: 45,
                        minRotation: 45,
                    },
                    title: {
                        display: true,
                        text: 'Time',
                    },
                },
                y: {
                    min: 0,
                    max: 14,
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: sensorName,
                    },
                },
            },
        };

        return (
            <div style={{ width: '100%', height: 400, marginBottom: 60 }}>
                <Bar data={chartData} options={options} />
                <div style={{ textAlign: 'center', marginTop: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {Array.from({ length: 15 }, (_, i) => {
                            const color = get_pointColor(i, 'fill');
                            return (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', margin: '0 5px' }}>
                                    <div
                                        style={{
                                            width: 20,
                                            height: 20,
                                            backgroundColor: color,
                                            borderRadius: '50%',
                                            marginRight: 5,
                                        }}
                                    />
                                    <span>{i}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    } else if (isWindDirection) {
        chartData = {
            labels: data.map(d => d.timestamp),
            datasets: moduleNames.map((mod, idx) => ({
                label: mod,
                data: data.map(d => d[mod] ?? null),
                borderColor: hslColors[idx % hslColors.length],
                borderWidth: 2,
                backgroundColor: 'transparent',
                pointRadius: 8,
                pointStyle: 'line',
                spanGaps: true,
                pointRadius: 0,
                pointStyle: 'circle',
            })),
        };

        options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: `${sensorName} History`,
                    font: { size: 18 },
                },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                },
                legend: {
                    position: 'top',
                    onClick: (e, legendItem, legend) => {
                        const index = legendItem.datasetIndex;
                        const chart = legend.chart;
                        const meta = chart.getDatasetMeta(index);
                        meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
                        chart.update();
                    },
                },
                directionArrows: {
                    enabled: true,
                    angles: moduleNames.map(mod => data.map(d => d[mod])),
                },
            },
            scales: {
                x: {
                    type: 'time',
                    time: { unit: 'hour', tooltipFormat: 'PPpp' },
                    ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 },
                    title: { display: true, text: 'Time' },
                },
                y: {
                    min: 0,
                    max: 360,
                    title: { display: true, text: `${sensorName} (°)` },
                },
            },
        };

        return (
            <div style={{ width: '100%', height: 300, marginBottom: 40 }}>
                <Line data={chartData} options={options} plugins={[directionArrowPlugin]} />
            </div>
        );
    } else {
        chartData = {
            labels: data.map(d => d.timestamp),
            datasets: moduleNames.map((mod, idx) => ({
                label: mod,
                data: data.map(d => d[mod]),
                borderColor: hslColors[idx % hslColors.length],
                borderWidth: 2,
                backgroundColor: 'transparent',
                spanGaps: true,
            })),
        };

        options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: `${sensorName} History`,
                    font: { size: 18 },
                },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                },
                legend: { position: 'top' },
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'hour',
                        tooltipFormat: 'PPpp',
                    },
                    ticks: {
                        autoSkip: false,
                        maxRotation: 45,
                        minRotation: 45,
                    },
                    title: {
                        display: true,
                        text: 'Time',
                    },
                },
                y: {
                    beginAtZero: false,
                    title: {
                        display: true,
                        text: sensorName,
                    },
                },
            },
        };

        return (
            <div style={{ width: '100%', height: 300, marginBottom: 40 }}>
                <Line data={chartData} options={options} />
            </div>
        );
    }
};

const StationDetailsPage = () => {
    const [stationData, setStationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [range, setRange] = useState('last24h');
    const { serial } = useParams();
    const [downloadRange, setDownloadRange] = useState([null, null]);
    const [startDate, endDate] = downloadRange;
    const [downloading, setDownloading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [chartConfigs, setChartConfigs] = useState(null);

    const fetchStationDetails = async (selectedRange) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(
                global.config.server_url + 'api/get/station/details/',
                {
                    params: {
                        serial_number: serial,
                        range: selectedRange,
                    }
                }
            );
            console.log(response.data);
            setChartConfigs(response.data.charts);
            setStationData(response.data);
        } catch (err) {
            setError('Failed to load station data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };


    const handleDownload = async () => {
        setDownloading(true);
        try {
            const start = startDate.toLocaleDateString('en-CA'); // 'YYYY-MM-DD'
            const end = endDate.toLocaleDateString('en-CA');
            console.log(serial, 'serial');
            const response = await axiosInstance.post(
                global.config.server_url + 'api/get/download/station/details/',
                {
                    serial_number: serial,
                    start_date: start,
                    end_date: end,
                },
                {
                    responseType: 'blob',
                }
            );

            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `sensor_data_${start}_to_${end}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            setDownloading(false);
        } catch (error) {
            setDownloading(false);
            console.error('Download error:', error);
        }
    };


    useEffect(() => {
        fetchStationDetails(range);
    }, [serial, range]);

    if (loading) return <div className="text-center mt-5"><Spinner animation="border" /></div>;

    if (error) return <Alert variant="danger">{error}</Alert>;

    const groupedData = stationData.grouped_data;

    return (
    <>
        <div className="col-12 col-md-9 mb-2">
             <h4><b>Station</b></h4>
             <small className="text-muted">Details</small>
        </div>
        <div className="card mt-1.5">
            <div className="card-body">
                <h4><b>Station: {stationData.station_location}</b></h4>
                <p>
                  <strong>Serial:</strong>{' '}
                  <span style={{ color: '#0B5D46' }}><strong>{stationData.station_no}</strong></span>
                </p>
                <div className="d-flex justify-content-between">
                    {/* Range Selector */}
                    <ButtonGroup className="mb-4">
                          <button
                            className={`custom-tab ${range === 'last24h' ? 'active' : ''}`}
                            onClick={() => setRange('last24h')}
                          >
                            24 Hours
                          </button>
                          <button
                            className={`custom-tab ${range === 'last3d' ? 'active' : ''}`}
                            onClick={() => setRange('last3d')}
                          >
                            3 Days
                          </button>
                          <button
                            className={`custom-tab ${range === 'last7d' ? 'active' : ''}`}
                            onClick={() => setRange('last7d')}
                          >
                            7 Days
                          </button>

                    </ButtonGroup>
                    <DropdownButton id="download-dropdown" variant="primary" title="Download Data">
                        <div style={{ padding: '10px', width: 250 }}>
                            <label className="form-label">Select Date Range</label>
                            <DatePicker
                            selectsRange
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(update) => {
                            setDownloadRange(update);
                            }}
                            isClearable={true}
                            dateFormat="yyyy-MM-dd"
                            className="form-control"
                            />

                            <Button
                            className="custom-tab mt-3 w-100"
                            size="small"
                            onClick={handleDownload}
                            disabled={!startDate || !endDate || downloading}
                            >
                                {downloading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Downloading...
                                    </>
                                ) : (
                                    'Download CSV'
                                )}
                            </Button>
                        </div>
                    </DropdownButton>
                </div>

                {/* Sensor Charts */}
                {Object.entries(stationData.grouped_data).map(([sensorType, modules]) => (
                  <SensorGraph key={sensorType} sensorType={sensorType} modules={modules} chartConfigs={chartConfigs}/>
                ))}
            </div>
        </div>
        </>
    );
};

export default StationDetailsPage;
